// src/components/ReminderSettingsPanel.jsx
// Deadline reminder emails - one set of rules for the whole organisation,
// shown under each cohort's deadlines in the Cohorts tab. Loads and saves
// only its own setting (reminderSettings in orgs/{orgId}/settings/marking),
// separately from Settings' "Save Changes", so neither can overwrite the
// other with an out-of-date copy.
import React, { useEffect, useState } from 'react';
import { Check, ThumbsUp, Bell, AlertTriangle } from 'lucide-react';
import { getReminderSettings, updateReminderSettings, getDefaultReminderSettings, getSettingsReadFailureCount } from '../firebase/settings';

function ReminderSettingsPanel() {
  const [reminderSettings, setReminderSettings] = useState(getDefaultReminderSettings());
  const [savedJson, setSavedJson] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const failuresBefore = getSettingsReadFailureCount();
      const data = await getReminderSettings();
      if (cancelled) return;
      if (getSettingsReadFailureCount() !== failuresBefore) {
        // Defaults came back because the read failed - don't let Save
        // overwrite the real settings with them
        setLoadFailed(true);
        return;
      }
      setReminderSettings(data);
      setSavedJson(JSON.stringify(data));
    })();
    return () => { cancelled = true; };
  }, []);

  const loaded = savedJson !== null;
  const dirty = loaded && JSON.stringify(reminderSettings) !== savedJson;

  const handleSave = async () => {
    if (!loaded || loadFailed) return;
    setSaving(true);
    try {
      await updateReminderSettings(reminderSettings);
      setSavedJson(JSON.stringify(reminderSettings));
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
    } catch (error) {
      console.error('Save reminder settings error:', error);
      alert('Failed to save reminder settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    }}>
      <h2 style={{ margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Bell size={22} />
        Deadline Reminder Emails
      </h2>
      <div style={{ display: 'inline-block', padding: '4px 10px', background: '#fff7e6', border: '1px solid #f39c12', color: '#8a5a00', borderRadius: '6px', fontSize: '13px', fontWeight: '600', marginBottom: '12px' }}>
        These apply to all cohorts, not just this one
      </div>
      <p style={{ color: '#7f8c8d', marginBottom: '24px' }}>
        Automatically email students before and/or after a homework deadline.
        Applies to every homework type that has a deadline set for a cohort.
        Use variables like {'{{studentName}}'} (first name), {'{{homeworkName}}'}, {'{{deadlineDate}}'}.
        Every reminder ends with your name and a link to the app, and replies
        come to your notification email.
        A student who's already submitted, or for whom this homework type is
        disabled, is never sent a reminder - and each student only ever gets
        one before-reminder and one after-reminder per deadline.
      </p>

      {loadFailed && (
        <div style={{ background: '#fdecea', border: '1px solid #c33329', color: '#8a1f17', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
          <AlertTriangle size={18} />
          Your reminder settings couldn't be loaded. Saving is turned off so they aren't overwritten - please refresh the page.
        </div>
      )}

      {[
        { kind: 'before', title: 'Before the Deadline', offsetLabel: 'Days before deadline', defaultDesc: 'Sent once, this many days before the deadline, to any student who hasn\'t submitted yet.' },
        { kind: 'after', title: 'After the Deadline', offsetLabel: 'Days after deadline', defaultDesc: 'Sent once, this many days after the deadline, to any student who still hasn\'t submitted.' }
      ].map(({ kind, title, offsetLabel, defaultDesc }) => (
        <div key={kind} style={{ padding: '20px', border: '2px solid #ecf0f1', borderRadius: '8px', background: 'white', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#2c3e50' }}>{title}</div>
            <div
              onClick={() => setReminderSettings(prev => ({ ...prev, [`${kind}Enabled`]: !prev[`${kind}Enabled`] }))}
              style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#f8f9fa', border: '2px solid #e2e8f0', borderRadius: '6px', cursor: 'pointer', userSelect: 'none' }}
            >
              <div style={{
                width: '22px', height: '22px', borderRadius: '4px', border: '2px solid',
                borderColor: reminderSettings[`${kind}Enabled`] ? '#1b998b' : '#bdc3c7',
                background: reminderSettings[`${kind}Enabled`] ? '#1b998b' : 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', flexShrink: 0
              }}>
                {reminderSettings[`${kind}Enabled`] && <Check size={14} />}
              </div>
              <span style={{ fontSize: '13px', color: '#4a5568' }}>{reminderSettings[`${kind}Enabled`] ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>
          <div style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '16px' }}>{defaultDesc}</div>

          <div style={{ marginBottom: '16px', maxWidth: '220px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>{offsetLabel}</label>
            <input
              type="number"
              min="0"
              value={reminderSettings[`${kind}DaysOffset`]}
              onChange={(e) => setReminderSettings(prev => ({ ...prev, [`${kind}DaysOffset`]: parseInt(e.target.value, 10) || 0 }))}
              style={{ width: '100%', padding: '10px 12px', border: '2px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }}
            />
          </div>

          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>Email Wording</label>
          <textarea
            value={reminderSettings[`${kind}Template`]}
            onChange={(e) => setReminderSettings(prev => ({ ...prev, [`${kind}Template`]: e.target.value }))}
            rows={8}
            style={{ width: '100%', padding: '12px', border: '2px solid #ecf0f1', borderRadius: '8px', fontSize: '14px', fontFamily: 'monospace', resize: 'vertical', lineHeight: '1.6' }}
          />
        </div>
      ))}

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <button
          onClick={handleSave}
          disabled={!loaded || loadFailed || saving || !dirty}
          style={{
            opacity: !loaded || loadFailed || saving || !dirty ? 0.5 : 1,
            padding: '14px 28px',
            background: '#c33329',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: !loaded || loadFailed || saving || !dirty ? 'not-allowed' : 'pointer',
            fontWeight: '600',
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <ThumbsUp size={18} />
          {saving ? 'Saving...' : 'Save Reminder Settings'}
        </button>
        {dirty && !saving && <span style={{ fontSize: '14px', color: '#c33329', fontWeight: '600' }}>Unsaved changes</span>}
        {justSaved && !dirty && <span style={{ fontSize: '14px', color: '#1b998b', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}><Check size={16} /> Saved</span>}
        {!loaded && !loadFailed && <span style={{ fontSize: '14px', color: '#7f8c8d' }}>Loading...</span>}
      </div>
    </div>
  );
}

export default ReminderSettingsPanel;
